import { t as uiText } from '../i18n';
import React, { useEffect, useState, useRef } from 'react';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, OAuthProvider, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { sendVerificationEmail } from '../features/emailVerification/api';
import appleSignInImg from '../assets/image/appleid_button.png';
import loginBgImg from '../assets/image/login-bg.png';
import logoYokoImg from '../assets/image/logo_yoko.png';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import { shouldUseExternalBrowser, logAuthAttempt, handleAuthError, isIOS } from '../utils/authUtils';
import { OpenExternallyDialog } from '../components/auth/OpenExternallyDialog';
import ImageCropModal from '../components/ImageCropModal';
import { useAuth } from '../context/AuthContext';
import { AuthLanguageSwitcher } from '../components/LanguageSwitcher';
import { syncPhotoUrlToStorage, shouldSyncPhotoUrl, syncExistingUserPhoto } from '../features/auth/photoUrlSyncUtils';
import { resolveSignupRegion } from '../utils/clientRegion';
import './Login.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loadUserImage, setIsUserRegistrationInProgress } = useAuth();
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [showExternalBrowserDialog, setShowExternalBrowserDialog] = useState(false);
  const [externalDialogTargetUrl, setExternalDialogTargetUrl] = useState<string>('');
  const processingRef = useRef(false);
  
  // メールログイン用のstate
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailLoginLoading, setIsEmailLoginLoading] = useState(false);
  
  // アカウント作成モーダル用のstate
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpEmailConfirm, setSignUpEmailConfirm] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDisplayName, setSignUpDisplayName] = useState('');
  const [signUpIcon, setSignUpIcon] = useState('');
  const [signUpIconFile, setSignUpIconFile] = useState<File | null>(null);
  const [isSignUpLoading, setIsSignUpLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailMatchError, setEmailMatchError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [forgotSuccessMessage, setForgotSuccessMessage] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  // 背景画像の読み込み状態を管理
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setBackgroundLoaded(true);
    };
    img.onerror = () => {
      console.warn('背景画像の読み込みに失敗しました');
      setBackgroundLoaded(true); // エラーでもローディング状態を終了
    };
    img.src = loginBgImg;
  }, []);

  // /sys/login?redirect=... を sessionStorage に反映（外部ブラウザ移行でも復帰できるようにする）
  useEffect(() => {
    try {
      const sanitizeRelativePath = (input: string | null): string | null => {
        if (!input) return null;
        const raw = input.trim();
        if (!raw) return null;
        if (!raw.startsWith('/')) return null;
        if (raw.startsWith('//')) return null;
        if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw)) return null;
        return raw;
      };
      const qs = new URLSearchParams(window.location.search);
      const redirect = sanitizeRelativePath(qs.get('redirect'));
      if (redirect) {
        sessionStorage.setItem('redirectAfterLogin', redirect);
      }
    } catch {
      // ignore
    }
  }, []);

  // 起動時：リダイレクト結果を回収（onAuthStateChangedは AuthProvider に集約）
  useEffect(() => {
    const initializeAuth = async () => {
      if (processingRef.current) return;
      processingRef.current = true;

      try {
        const result = await getRedirectResult(auth as any);
        if (result?.user) {
          logAuthAttempt('redirect_result_success', true);
          await handleUserRegistration(result.user);
          navigate('/');
          return;
        }
      } catch (e: any) {
        logAuthAttempt('redirect_result_error', false, e);
        console.error('リダイレクト結果の処理に失敗:', e);
      } finally {
        processingRef.current = false;
      }
    };

    initializeAuth();
  }, [navigate]);

  const handleUserRegistration = async (user: any) => {
    // Firestore にユーザー情報を保存
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    let photoPath: string | null = null;

    // Google/Apple認証の場合、外部photoURLをStorageに同期
    if (user.photoURL && shouldSyncPhotoUrl(user.photoURL)) {
      try {
        photoPath = await syncPhotoUrlToStorage(user.photoURL, user.uid);
        if (photoPath) {}
      } catch (error) {
        console.warn('[Login] 外部photoURL同期失敗（続行）', { uid: user.uid, error });
        // 同期失敗でもアカウント作成は続行
      }
    }

    if (!userSnap.exists()) {
      // 新規登録時にのみ「登録した国」を推定して保存する。
      // - GeoIP（MaxMind）+ ブラウザ TZ をサーバー側で突合
      // - 失敗しても登録自体は止めない（country: null で続行）
      const region = await resolveSignupRegion();

      await setDoc(userRef, {
        uid: user.uid,
        displayName: user.displayName ?? null,
        email: user.email ?? null,
        photoURL: user.photoURL ?? null,
        photoPath: photoPath,
        createdAt: serverTimestamp(),
        signupCountry: region.country,
        signupCountryConfidence: region.confidence,
        signupTimezone: region.timezone,
        signupLanguage: region.language,
        signupAt: serverTimestamp(),
      });
    } else {
      // 既存ユーザーの場合、photoPathを更新（photoURLは既存のものを保持）
      const existingData = userSnap.data();
      const existingPhotoPath = existingData?.photoPath || null;
      const existingPhotoURL = existingData?.photoURL || user.photoURL || null;
      
      // photoPathがない場合は外部photoURLから同期を試行
      let finalPhotoPath = photoPath || existingPhotoPath;
      if (!finalPhotoPath && existingPhotoURL) {
        try {
          const syncedPhotoPath = await syncExistingUserPhoto(user.uid, existingPhotoURL, existingPhotoPath);
          if (syncedPhotoPath) {
            finalPhotoPath = syncedPhotoPath;
          }
        } catch (error) {
          console.warn('[Login] 既存ユーザーphotoPath同期失敗（続行）', { uid: user.uid, error });
        }
      }
      
      await setDoc(userRef, {
        ...existingData,
        photoPath: finalPhotoPath,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  };

  // プロフィール情報を含むユーザー登録
  const handleUserRegistrationWithProfile = async (user: any, displayName: string, iconFile: File | null) => {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    let photoPath: string | null = null;

    // 画像ファイルがある場合はStorageにアップロード
    if (iconFile) {
      try {
        const timestamp = Date.now();
        const fileName = `userIcons/${user.uid}/${timestamp}_${iconFile.name}`;
        const storageRef = ref(storage, fileName);

        // 画像をアップロード
        const snapshot = await uploadBytes(storageRef, iconFile);
        photoPath = fileName;
      } catch (error) {
        console.error('❌ [handleUserRegistrationWithProfile] ユーザーアイコンのアップロードに失敗:', error);
        // アップロードに失敗してもアカウント作成は続行
      }
    } else {}

    if (!userSnap.exists()) {
      // 新規登録時にのみ国情報を保存（メール+パスワード経由のサインアップ）
      const region = await resolveSignupRegion();

      await setDoc(userRef, {
        uid: user.uid,
        displayName: displayName,
        email: user.email ?? null,
        photoURL: user.photoURL ?? null,
        photoPath: photoPath,
        createdAt: serverTimestamp(),
        signupCountry: region.country,
        signupCountryConfidence: region.confidence,
        signupTimezone: region.timezone,
        signupLanguage: region.language,
        signupAt: serverTimestamp(),
      });
    } else {
      // 既存ユーザーの場合、プロフィール情報を更新
      await setDoc(userRef, {
        ...userSnap.data(),
        displayName: displayName,
        photoPath: photoPath || userSnap.data()?.photoPath || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  };

  const handleGoogleLogin = async () => {
    // In-App / WebView → 外部ブラウザ誘導
    if (shouldUseExternalBrowser()) {
      logAuthAttempt('iab_or_webview_detected', false);
      try {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        const target =
          redirectPath && redirectPath.startsWith('/')
            ? `${window.location.origin}/sys/login?redirect=${encodeURIComponent(redirectPath)}`
            : `${window.location.origin}/sys/login`;
        setExternalDialogTargetUrl(target);
      } catch {
        setExternalDialogTargetUrl('');
      }
      setShowExternalBrowserDialog(true);
      return;
    }

    const provider = new GoogleAuthProvider();

    // 通常ブラウザ：まずPopup、失敗時Redirect
    try {
      const result = await signInWithPopup(auth as any, provider);
      if (result?.user) {
        logAuthAttempt('popup_success', true);
        await handleUserRegistration(result.user);
        navigate('/');
        return;
      }
    } catch (e: any) {
      logAuthAttempt('popup_failed', false, e);
      const info = handleAuthError(e);
      try {
        // 代表的なPopup失敗はRedirectにフォールバック
        if (!info.code || info.isPopupFailure) {
          await signInWithRedirect(auth as any, provider);
          return;
        }
        // それ以外でも最終的にはRedirectを試す（安全側）
        await signInWithRedirect(auth as any, provider);
        return;
      } catch (e2: any) {
        logAuthAttempt('redirect_failed', false, e2);
        console.error('リダイレクト認証も失敗:', e2);
        alert(uiText("Failed to log in. Please try another browser (Safari or Chrome)."));
      }
    }
  };

  const handleAppleLogin = async () => {
    // In-App / WebView → 外部ブラウザ誘導
    if (shouldUseExternalBrowser()) {
      logAuthAttempt('iab_or_webview_detected_apple', false);
      try {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        const target =
          redirectPath && redirectPath.startsWith('/')
            ? `${window.location.origin}/sys/login?redirect=${encodeURIComponent(redirectPath)}`
            : `${window.location.origin}/sys/login`;
        setExternalDialogTargetUrl(target);
      } catch {
        setExternalDialogTargetUrl('');
      }
      setShowExternalBrowserDialog(true);
      return;
    }

    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');

    try {
      // iOSではPopupの失敗率が高いため、Appleは初手からリダイレクト
      if (isIOS()) {
        await signInWithRedirect(auth as any, provider);
        return; // リダイレクト後はuseEffectで回収
      }

      const result = await signInWithPopup(auth as any, provider);
      if (result?.user) {
        logAuthAttempt('apple_popup_success', true);
        await handleUserRegistration(result.user);
        navigate('/');
        return;
      }
    } catch (error: any) {
      logAuthAttempt('apple_popup_failed', false, error);
      // Apple ポップアップ認証の失敗理由を詳細ログ
      console.error('Appleでのポップアップ認証が失敗:', {
        code: error?.code,
        message: error?.message,
        customData: error?.customData,
        name: error?.name,
        stack: error?.stack,
        raw: error,
      });
      try {
        await signInWithRedirect(auth as any, provider);
        // リダイレクト後は useEffect で結果を処理
      } catch (redirectError) {
        logAuthAttempt('apple_redirect_failed', false, redirectError);
        // Apple リダイレクト認証の失敗理由を詳細ログ
        console.error('Appleでのリダイレクト認証も失敗:', {
          code: (redirectError as any)?.code,
          message: (redirectError as any)?.message,
          customData: (redirectError as any)?.customData,
          name: (redirectError as any)?.name,
          stack: (redirectError as any)?.stack,
          raw: redirectError,
        });
        alert(uiText("Sign-in with Apple failed. Please try another browser (Safari or Chrome)."));
      }
    }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) {
      alert(uiText("Enter your email address and password."));
      return;
    }

    setIsEmailLoginLoading(true);
    try {
      // Firebase Authenticationでメールログイン
      const userCredential = await signInWithEmailAndPassword(auth as any, email, password);
      const user = userCredential.user;
      const loginUid = user.uid;

      logAuthAttempt('email_login_success', true);

      await handleUserRegistration(user);
      
      // メールログイン時も既存ユーザーのphotoPath同期を実行
      try {
        const userRef = doc(db, 'users', loginUid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const existingPhotoPath = userData?.photoPath || null;
          const existingPhotoURL = userData?.photoURL || user.photoURL || null;
          
          if (!existingPhotoPath && existingPhotoURL) {
            const syncedPhotoPath = await syncExistingUserPhoto(loginUid, existingPhotoURL, existingPhotoPath);
            if (syncedPhotoPath) {
              await setDoc(userRef, {
                photoPath: syncedPhotoPath,
                updatedAt: serverTimestamp(),
              }, { merge: true });
            }
          }
        }
      } catch (error) {
        console.warn('[Login] メールログイン時photoPath同期失敗（続行）', { uid: loginUid, error });
      }
      
      await loadUserImage(loginUid);

      // ホーム画面にリダイレクト
      navigate('/');
    } catch (error: any) {
      logAuthAttempt('email_login_failed', false, error);
      console.error('メールログインエラー:', error);
      
      // Firebase Authenticationのエラーメッセージを日本語化
      let errorMessage = uiText("Failed to log in.");
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = uiText("No account was found for this email address.");
          break;
        case 'auth/wrong-password':
          errorMessage = uiText("Password is incorrect.");
          break;
        case 'auth/invalid-email':
          errorMessage = uiText("Invalid email address.");
          break;
        case 'auth/user-disabled':
          errorMessage = uiText("This account has been disabled.");
          break;
        case 'auth/too-many-requests':
          errorMessage = uiText("Too many login attempts. Please wait a moment and try again.");
          break;
        case 'auth/network-request-failed':
          errorMessage = uiText("A network error occurred. Check your internet connection.");
          break;
        default:
          errorMessage = uiText("Failed to log in: {{message}}", { message: error.message });
      }
      
      alert(errorMessage);
    } finally {
      setIsEmailLoginLoading(false);
    }
  };

  const handleSignUp = () => {
    setShowSignUpModal(true);
  };

  const handleOpenForgotModal = () => {
    setForgotEmail('');
    setForgotEmailError('');
    setForgotSuccessMessage('');
    setIsForgotLoading(false);
    setShowForgotModal(true);
  };

  const handleCloseForgotModal = () => {
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotEmailError('');
    setForgotSuccessMessage('');
    setIsForgotLoading(false);
  };

  const handleForgotEmailChange = (value: string) => {
    setForgotEmail(value);
    if (value && !validateEmail(value)) {
      setForgotEmailError(uiText("Enter a valid email address."));
    } else {
      setForgotEmailError('');
    }
  };

  const handleSendPasswordReset = async () => {
    if (!forgotEmail) {
      setForgotEmailError(uiText("Enter your email address."));
      return;
    }
    if (!validateEmail(forgotEmail)) {
      setForgotEmailError(uiText("Enter a valid email address."));
      return;
    }

    setIsForgotLoading(true);
    setForgotSuccessMessage('');
    try {
      await sendPasswordResetEmail(auth as any, forgotEmail);
      setForgotSuccessMessage(uiText("A password reset email was sent. Please check your inbox. The sender is noreply@notreu.com. If you don't see it, check your spam folder."));
    } catch (error: any) {
      console.error('パスワード再設定メール送信エラー:', error);
      let errorMessage = uiText("Failed to send email. Please try again later.");
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = uiText("No account was found for this email address.");
          break;
        case 'auth/invalid-email':
          errorMessage = uiText("Invalid email address.");
          break;
        case 'auth/network-request-failed':
          errorMessage = uiText("A network error occurred. Check your internet connection.");
          break;
        default:
          errorMessage = uiText("Failed to send email: {{message}}", { message: error.message });
      }
      setForgotEmailError(errorMessage);
    } finally {
      setIsForgotLoading(false);
    }
  };

  // メールアドレス形式チェック関数
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // パスワード強度チェック関数
  const validatePassword = (password: string): { isValid: boolean; message: string } => {
    if (password.length < 8) {
      return { isValid: false, message: uiText("Enter a password of at least 8 characters.") };
    }

    let typeCount = 0;
    const hasNumber = /\d/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (hasNumber) typeCount++;
    if (hasLowerCase) typeCount++;
    if (hasUpperCase) typeCount++;
    if (hasSymbol) typeCount++;

    if (typeCount < 3) {
      return { 
        isValid: false, 
        message: uiText("Use at least 3 of the following: numbers, lowercase, uppercase, and symbols.") 
      };
    }

    return { isValid: true, message: '' };
  };

  // メールアドレス入力時のリアルタイムバリデーション
  const handleEmailChange = (value: string) => {
    setSignUpEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(uiText("Enter a valid email address."));
    } else {
      setEmailError('');
    }
    
    // メールアドレス一致チェック
    if (value && signUpEmailConfirm) {
      if (value !== signUpEmailConfirm) {
        setEmailMatchError(uiText("Email addresses do not match."));
      } else {
        setEmailMatchError('');
      }
    }
  };

  // メールアドレス確認入力時のリアルタイムバリデーション
  const handleEmailConfirmChange = (value: string) => {
    setSignUpEmailConfirm(value);
    
    // メールアドレス一致チェック
    if (signUpEmail && value) {
      if (signUpEmail !== value) {
        setEmailMatchError(uiText("Email addresses do not match."));
      } else {
        setEmailMatchError('');
      }
    }
  };

  // パスワード入力時のリアルタイムバリデーション
  const handlePasswordChange = (value: string) => {
    setSignUpPassword(value);
    
    if (value) {
      const validation = validatePassword(value);
      if (!validation.isValid) {
        setPasswordError(validation.message);
      } else {
        setPasswordError('');
      }
    } else {
      setPasswordError('');
    }
  };

  // アカウント名入力時のリアルタイムバリデーション
  const handleDisplayNameChange = (value: string) => {
    setSignUpDisplayName(value);
    
    if (value.trim().length === 0) {
      setDisplayNameError(uiText("Enter an account name."));
    } else if (value.trim().length < 2) {
      setDisplayNameError(uiText("Enter an account name of at least 2 characters."));
    } else if (value.trim().length > 20) {
      setDisplayNameError(uiText("Enter an account name of 20 characters or fewer."));
    } else {
      setDisplayNameError('');
    }
  };


  // 画像クリア
  const handleImageClear = () => {
    setSignUpIcon('');
    setSignUpIconFile(null);
    setSelectedImageFile(null);
  };

  const handleSignUpSubmit = async () => {
    if (!signUpEmail || !signUpEmailConfirm || !signUpPassword || !signUpDisplayName.trim()) {
      alert(uiText("Please complete all required fields."));
      return;
    }

    // メールアドレス形式チェック
    if (!validateEmail(signUpEmail)) {
      alert(uiText("Enter a valid email address."));
      return;
    }

    if (signUpEmail !== signUpEmailConfirm) {
      alert(uiText("Email addresses do not match."));
      return;
    }

    // パスワード強度チェック
    const passwordValidation = validatePassword(signUpPassword);
    if (!passwordValidation.isValid) {
      alert(passwordValidation.message);
      return;
    }

    // アカウント名チェック
    if (signUpDisplayName.trim().length < 2) {
      alert(uiText("Enter an account name of at least 2 characters."));
      return;
    }
    if (signUpDisplayName.trim().length > 20) {
      alert(uiText("Enter an account name of 20 characters or fewer."));
      return;
    }

    setIsSignUpLoading(true);
    setIsUserRegistrationInProgress(true);
    
    try {
      // 1. Firebase Authenticationでアカウント作成
      const userCredential = await createUserWithEmailAndPassword(auth as any, signUpEmail, signUpPassword);
      const user = userCredential.user;
      const registrationUid = user.uid;

      logAuthAttempt('email_signup_success', true);

      // 確認メールをバックエンド経由で送信（失敗してもモーダル/バナーから再送できる）
      void sendVerificationEmail('ncn').catch((e) => {
        console.warn('⚠️ [handleSignUpSubmit] 確認メールの送信に失敗（再送可能）:', e);
      });

      await handleUserRegistrationWithProfile(user, signUpDisplayName.trim(), signUpIconFile);

      // 3. 必要に応じてプロフィール更新
      try {
        if ((auth as any)?.currentUser) {
          await updateProfile((auth as any).currentUser, { 
            displayName: signUpDisplayName.trim() 
          });
          await (auth as any).currentUser.reload();
        } else {
          console.warn('⚠️ [handleSignUpSubmit] auth.currentUserがnullのため、プロフィール更新をスキップ');
        }
      } catch (profileError) {
        console.warn('⚠️ [handleSignUpSubmit] プロフィール更新に失敗（続行）:', profileError);
      }

      // 4. 登録プロセス完了
      setIsUserRegistrationInProgress(false);

      await loadUserImage(registrationUid);

      // 6. モーダルを閉じてホーム画面にリダイレクト
      setShowSignUpModal(false);
      navigate('/');
    } catch (error: any) {
      logAuthAttempt('email_signup_failed', false, error);
      console.error('アカウント作成エラー:', error);
      
      // エラー時は登録プロセスフラグをリセット
      setIsUserRegistrationInProgress(false);
      
      // Firebase Authenticationのエラーメッセージを日本語化
      let errorMessage = uiText("Failed to create the account.");
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = uiText("This email address is already in use.");
          break;
        case 'auth/invalid-email':
          errorMessage = uiText("Invalid email address.");
          break;
        case 'auth/weak-password':
          errorMessage = uiText("Password is too weak.");
          break;
        case 'auth/operation-not-allowed':
          errorMessage = uiText("Email/password authentication is not enabled.");
          break;
        default:
          errorMessage = uiText("Failed to create the account: {{message}}", { message: error.message });
      }
      
      alert(errorMessage);
    } finally {
      setIsSignUpLoading(false);
    }
  };

  const handleCloseSignUpModal = () => {
    setShowSignUpModal(false);
    setSignUpEmail('');
    setSignUpEmailConfirm('');
    setSignUpPassword('');
    setSignUpDisplayName('');
    setSignUpIcon('');
    setSignUpIconFile(null);
    setEmailError('');
    setEmailMatchError('');
    setPasswordError('');
    setDisplayNameError('');
    setShowCropModal(false);
    setSelectedImageFile(null);
  };

  return (
    <div className={`login-container ${backgroundLoaded ? 'loaded' : 'loading'}`}>
      <div className="login-content">
        <AuthLanguageSwitcher />
        <div className="login-logo">
          <img src={logoYokoImg} alt="notreu logo" />
        </div>
        <button onClick={handleGoogleLogin} className="gsi-material-button" style={{ width: 240 }}>
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{ display: 'block' }}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="gsi-material-button-contents">Sign in with Google</span>
            <span style={{ display: 'none' }}>Sign in with Google</span>
          </div>
        </button>
        <button onClick={handleAppleLogin} className="appleid-image-button" aria-label="Sign in with Apple">
          <img src={appleSignInImg} alt="Sign in with Apple" width={240} height={40} />
        </button>
        
        {/* または */}
        <div className="login-divider">
          <span className="login-divider-text">{uiText("or")}</span>
        </div>
        
        {/* メールログイン */}
        <div className="email-login-section">
          <div className="email-input-group">
            <input
              type="email"
              placeholder={uiText("Email address")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="email-input"
              disabled={isEmailLoginLoading}
            />
          </div>
          <div className="password-input-group">
            <input
              type="password"
              placeholder={uiText("Password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
              disabled={isEmailLoginLoading}
            />
          </div>
          <button
            onClick={handleEmailLogin}
            className="email-login-button"
            disabled={isEmailLoginLoading}
          >
            {isEmailLoginLoading ? uiText("Logging in...") : uiText("Log in with email")}
          </button>
          
          <button
            onClick={handleSignUp}
            className="signup-button"
            disabled={isEmailLoginLoading}
          >
            {uiText("Create account")}
          </button>
          <div className="forgot-password-text" onClick={handleOpenForgotModal} role="button" tabIndex={0}>{uiText("Forgot password")}</div>
        </div>
      </div>

      {/* In-App / WebView 検出時の外部誘導モーダル */}
      <OpenExternallyDialog
        isOpen={showExternalBrowserDialog}
        onClose={() => setShowExternalBrowserDialog(false)}
        appName="notreu"
        targetUrl={externalDialogTargetUrl || undefined}
      />

      {/* アカウント作成モーダル */}
      {showSignUpModal && (
        <div className="modal-overlay" onClick={handleCloseSignUpModal}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signup-modal-header">
              <h3 className="signup-modal-title">{uiText("Create account")}</h3>
              <button 
                className="signup-modal-close" 
                onClick={handleCloseSignUpModal}
                aria-label={uiText("Close")}
              >
                ×
              </button>
            </div>
            
            <div className="signup-modal-content">
              <div className="signup-input-group">
                <label className="signup-label">{uiText("Account name")} <span className="required">*</span></label>
                <input
                  type="text"
                  placeholder={uiText("Enter an account name (2-20 characters)")}
                  value={signUpDisplayName}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                  className={`signup-input ${displayNameError ? 'signup-input-error' : ''}`}
                  disabled={isSignUpLoading}
                />
                {displayNameError && <div className="signup-error-message">{displayNameError}</div>}
              </div>

              <div className="signup-input-group">
                <label className="signup-label">{uiText("Icon (optional, can be changed later)")}</label>
                
                {/* アイコン表示エリア */}
                <div className="icon-preview-area">
                  <div className="icon-preview">
                    {signUpIcon ? (
                      <img
                        src={signUpIcon}
                        alt={uiText("Account icon")}
                        className="icon-preview-image"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const textElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (textElement) {
                            textElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    {!signUpIcon && (
                      <div className="icon-preview-text">
                        {signUpDisplayName?.charAt(0) || 'A'}
                      </div>
                    )}
                  </div>
                  
                  <div className="icon-preview-info">
                    <div className="icon-preview-label">{uiText("Preview")}</div>
                    <div className="icon-preview-desc">
                      {signUpIcon ? uiText("Image icon") : uiText("Default icon")}
                    </div>
                  </div>
                </div>

                {/* 画像アップロード */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          setSelectedImageFile(file);
                          setShowCropModal(true);
                        }
                      };
                      input.click();
                    }}
                    disabled={isSignUpLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--workspace-primary, #cfae91)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSignUpLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      opacity: isSignUpLoading ? 0.6 : 1,
                    }}
                  >
                    {uiText("Select file")}
                  </button>
                  <div
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '14px',
                      color: '#666',
                    }}
                  >
                    {signUpIconFile ? signUpIconFile.name : uiText("None selected")}
                  </div>
                </div>

                {signUpIcon && (
                  <button
                    type="button"
                    onClick={handleImageClear}
                    className="image-clear-button"
                    disabled={isSignUpLoading}
                    style={{
                      marginTop: '8px',
                      padding: '4px 8px',
                      backgroundColor: '#ff6b6b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: isSignUpLoading ? 'not-allowed' : 'pointer',
                      opacity: isSignUpLoading ? 0.6 : 1,
                    }}
                  >
                    {uiText("Delete image")}
                  </button>
                )}
              </div>

              <div className="signup-input-group">
                <label className="signup-label">{uiText("Email address")} <span className="required">*</span></label>
                <input
                  type="email"
                  placeholder={uiText("Enter email address")}
                  value={signUpEmail}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={`signup-input ${emailError ? 'signup-input-error' : ''}`}
                  disabled={isSignUpLoading}
                />
                {emailError && <div className="signup-error-message">{emailError}</div>}
              </div>
              
              <div className="signup-input-group">
                <label className="signup-label">{uiText("Email address (confirmation)")} <span className="required">*</span></label>
                <input
                  type="email"
                  placeholder={uiText("Re-enter email address")}
                  value={signUpEmailConfirm}
                  onChange={(e) => handleEmailConfirmChange(e.target.value)}
                  className={`signup-input ${emailMatchError ? 'signup-input-error' : ''}`}
                  disabled={isSignUpLoading}
                />
                {emailMatchError && <div className="signup-error-message">{emailMatchError}</div>}
              </div>
              
              <div className="signup-input-group">
                <label className="signup-label">{uiText("Password")} <span className="required">*</span></label>
                <input
                  type="password"
                  placeholder={uiText("Password")}
                  value={signUpPassword}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={`signup-input ${passwordError ? 'signup-input-error' : ''}`}
                  disabled={isSignUpLoading}
                />
                {passwordError && <div className="signup-error-message">{passwordError}</div>}
                <div className="signup-hint-message">{uiText("Use at least 8 characters, including at least 3 of the following: numbers, lowercase letters, uppercase letters, and symbols.")}</div>
              </div>
              
              <button
                onClick={handleSignUpSubmit}
                className="signup-submit-button"
                disabled={isSignUpLoading}
              >
                {isSignUpLoading ? uiText("Creating...") : uiText("Create an account and sign in")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 画像クロップモーダル */}
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          cropType="circle"
          onCropComplete={(croppedFile, dataUrl) => {
            setSignUpIconFile(croppedFile);
            setSignUpIcon(dataUrl);
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
        />
      )}

      {/* パスワード再設定モーダル */}
      {showForgotModal && (
        <div className="modal-overlay" onClick={handleCloseForgotModal}>
          <div className="signup-modal forgot-modal" onClick={(e) => e.stopPropagation()}>
            <div className="signup-modal-header">
              <h3 className="signup-modal-title">{uiText("Set a new password")}</h3>
              <button
                className="signup-modal-close"
                onClick={handleCloseForgotModal}
                aria-label={uiText("Close")}
              >
                ×
              </button>
            </div>
            <div className="signup-modal-content">
              <div className="signup-input-group">
                <label className="signup-label">{uiText("Email address")} <span className="required">*</span></label>
                <input
                  type="email"
                  placeholder={uiText("Enter email address")}
                  value={forgotEmail}
                  onChange={(e) => handleForgotEmailChange(e.target.value)}
                  className={`signup-input ${forgotEmailError ? 'signup-input-error' : ''}`}
                  disabled={isForgotLoading}
                />
                {forgotEmailError && <div className="signup-error-message">{forgotEmailError}</div>}
              </div>

              <button
                onClick={handleSendPasswordReset}
                className="signup-submit-button"
                disabled={isForgotLoading}
              >
                {isForgotLoading ? uiText("Sending...") : uiText("Reset password")}
              </button>

              {forgotSuccessMessage && (
                <div className="signup-hint-message" style={{ color: 'var(--workspace-primary, #cfae91)', marginTop: 8, fontWeight: 700 }}>
                  {forgotSuccessMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
